import React, { useId } from "react";
import FilterWrapper from "./FilterWrapper";
import { useTranslations } from "next-intl";
import { Input } from "@/components/ui/input";

type Props = {
  icon: React.ReactNode;
  title: string;
  min: number | undefined;
  max: number | undefined;
  setMin: (value: number | undefined) => void;
  setMax: (value: number | undefined) => void;
  clear?: () => void;
  placeholder?: {
    min: string;
    max: string;
  };
  /** Unit shown inside both inputs, e.g. "€" or "m²". */
  unit?: string;
  children?: React.ReactNode;
};

const FilterRange: React.FC<Props> = ({
  max,
  min,
  setMax,
  setMin,
  icon,
  title,
  clear,
  placeholder,
  unit,
  children,
}) => {
  const t = useTranslations("search");
  const id = useId();
  const invalid = min !== undefined && max !== undefined && min > max;

  const field = (
    kind: "min" | "max",
    value: number | undefined,
    setValue: (value: number | undefined) => void,
  ) => (
    <label
      htmlFor={`${id}-${kind}`}
      className={`flex min-w-0 flex-1 cursor-text flex-col rounded-xl border bg-background px-3 py-1.5 transition-colors focus-within:border-primary focus-within:ring-1 focus-within:ring-primary ${
        invalid ? "border-rose-500" : "border-input"
      }`}
    >
      <span className="text-[11px] font-medium leading-4 text-muted-foreground">
        {kind === "min" ? t("input.minimum") : t("input.maximum")}
      </span>
      <span className="flex items-center gap-1">
        {unit && value !== undefined && (
          <span className="text-sm font-medium text-muted-foreground">
            {unit}
          </span>
        )}
        <Input
          id={`${id}-${kind}`}
          type="number"
          inputMode="numeric"
          min={0}
          aria-label={`${title}: ${
            kind === "min" ? t("input.minimum") : t("input.maximum")
          }`}
          aria-invalid={invalid || undefined}
          value={value ?? ""}
          onChange={(e) => {
            if (e.target.value === "") {
              setValue(undefined);
              return;
            }

            setValue(e.target.valueAsNumber);
          }}
          placeholder={
            kind === "min"
              ? placeholder?.min ?? t("input.0")
              : placeholder?.max ?? t("input.no-max")
          }
          className="h-6 min-w-0 rounded-none border-0 bg-transparent p-0 text-base font-medium shadow-none [appearance:textfield] focus:border-0 focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0 sm:text-sm [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
        />
      </span>
    </label>
  );

  return (
    <FilterWrapper
      icon={icon}
      title={title}
      clear={clear}
      showClear={min !== undefined || max !== undefined}
    >
      {children}
      <div className="flex w-full flex-row items-center gap-2">
        {field("min", min, setMin)}
        <span aria-hidden className="h-px w-2 flex-none bg-border" />
        {field("max", max, setMax)}
      </div>
    </FilterWrapper>
  );
};

export default FilterRange;
